import React from 'react';
import { MdClose } from 'react-icons/md';
import { IframePanel } from './IframePanel';
import { IframeManagerResult } from '../hooks/useIframeManager';
import { getIframeUrl } from '../utils/iframe';

export interface SplitScreenLayoutProps {
  chatPanel: React.ReactNode;
  iframeManager: IframeManagerResult;
}

/**
 * Manages split-screen layout
 * PRESERVES EXISTING BEHAVIOR & STYLING
 */
export function SplitScreenLayout({
  chatPanel,
  iframeManager,
}: SplitScreenLayoutProps) {
  const { state, actions, displayedMessage } = iframeManager;
  
  // Fullscreen iframe mode
  if (state.isFullscreen && displayedMessage?.iframeContent) {
    const iframeUrl = getIframeUrl(displayedMessage.iframeContent);
    
    if (iframeUrl) {
      return (
        <div className="fixed inset-0 z-50 bg-white">
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={actions.toggleFullscreen}
              className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
              aria-label="Exit fullscreen"
              title="Exit fullscreen"
            >
              <MdClose className="w-6 h-6 text-gray-600" />
            </button>
          </div>
          <iframe
            src={iframeUrl}
            className="w-full h-full border-0"
            title="Widget Fullscreen View"
          />
        </div>
      );
    }
  }
  
  // Split screen mode (desktop only - on mobile, iframes show inline)
  if (state.showPanel && displayedMessage) {
    return (
      <>
        {/* Mobile: Full width chat (iframes inline) */}
        <div className="md:hidden w-full h-screen">
          {chatPanel}
        </div>
        
        {/* Desktop: Split screen layout */}
        <div className="hidden md:flex h-screen w-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              state.isExpanded ? 'w-0' : 'w-1/2'
            }`}
            style={{ minWidth: state.isExpanded ? 0 : undefined }}
          >
            {chatPanel}
          </div>
          
          <div
            className={`h-full transition-all duration-300 ${
              state.isExpanded ? 'w-full' : 'w-1/2'
            }`}
          >
            <IframePanel
              message={displayedMessage}
              isExpanded={state.isExpanded}
              isVisible={state.showPanel}
            />
          </div>
        </div>
      </>
    );
  }
  
  // Chat only
  return <>{chatPanel}</>;
}

